import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { BrowserRouter, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  Clock, 
  FileText, 
  Car, 
  Shield, 
  Users, 
  MapPin, 
  Phone, 
  Mail, 
  Calendar,
  Star,
  ArrowRight,
  AlertTriangle,
  TrendingUp,
  Clipboard,
  Award,
  Instagram,
  PhoneCall,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import './App.css';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.25, 0.8, 0.25, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.section
      ref={ref}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 premium-glass">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-22">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_auto-experts-1/artifacts/32aw4g3v_white-Photoroom.png"
              alt="Crashcontrol Logo"
              className="h-12 md:h-16 w-auto"
            />
          </Link>
          
          {/* Right side - Social Media & Phone */}
          <div className="flex items-center space-x-1 md:space-x-4">
            {/* Social Media */}
            <div className="flex items-center">
              <a 
                href="https://wa.me/491623517659?text=Hallo%20Crashcontrol,%20ich%20interessiere%20mich%20für%20ein%20KFZ-Gutachten." 
                target="_blank"
                rel="noopener noreferrer"
                className="p-0.5 md:p-2 text-gray-400 hover:text-green-500 transition-colors duration-300 hover:bg-gray-800 rounded-full"
                aria-label="WhatsApp"
              >
                <svg className="w-3 h-3 md:w-5 md:h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
              </a>
            </div>
            
            {/* Divider */}
            <div className="h-6 w-px bg-gray-600"></div>
            
            {/* Phone */}
            <a 
              href="tel:+491623517659" 
              className="gold-button flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 font-semibold"
            >
              <PhoneCall className="w-4 h-4" />
              <span className="hidden sm:inline">Anrufen</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

const Hero = () => {
  const navigate = useNavigate();
  
  return (
    <div className="relative min-h-screen premium-pattern overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(255,215,0,0.1)_0%,_transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,_rgba(59,130,246,0.05)_0%,_transparent_50%)]"></div>
      </div>
      
      {/* Hero Content */}
      <div className="relative z-10 max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-8 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center px-4 py-2 premium-glass rounded-full text-sm font-medium premium-border"
              >
                <Award className="w-4 h-4 mr-2 premium-icon" />
                <span className="gold-text-subtle">Zertifizierter KFZ-Sachverständiger</span>
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl md:text-5xl font-bold text-white leading-tight"
              >
                <span className="gold-text">Unabhängige</span>{' '}
                <span className="text-white">KFZ-Gutachten</span>{' '}
                <span className="gold-text-subtle">vom Experten</span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg gold-accent font-medium"
              >
                Schnell, Fair & Gerichtsfest
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-base text-gray-300 max-w-xl leading-relaxed"
              >
                Schadengutachten, Wertgutachten & Leasingrücknahmen – 
                <span className="gold-text-subtle"> professionell und neutral</span> für Ihre finanzielle Sicherheit.
              </motion.p>
            </div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-3 md:gap-4"
            >
              <a 
                href="tel:+491623517659"
                className="group premium-glass hover:luxury-glow text-white px-4 md:px-6 py-2.5 md:py-3 rounded-2xl font-semibold transition-all duration-300 premium-border shadow-lg text-sm md:text-base"
              >
                <span className="flex items-center justify-center">
                  <Phone className="w-4 h-4 md:w-5 md:h-5 mr-1.5 md:mr-2" />
                  <span className="gold-text-subtle">Beratung</span>
                  <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-1.5 md:ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
              </a>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row sm:items-center sm:space-x-6 space-y-3 sm:space-y-0 text-xs sm:text-sm text-gray-400"
            >
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-1.5 md:mr-2 flex-shrink-0" />
                <span className="gold-text-subtle">Gerichtsfest</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 md:w-5 md:h-5 premium-icon mr-1.5 md:mr-2 flex-shrink-0" />
                <span className="gold-text-subtle">Schnelle Bearbeitung</span>
              </div>
              <div className="flex items-center">
                <Shield className="w-4 h-4 md:w-5 md:h-5 premium-icon mr-1.5 md:mr-2 flex-shrink-0" />
                <span className="gold-text-subtle">Unabhängig</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative mt-8 lg:mt-0"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl luxury-glow">
              <img
                src="https://images.unsplash.com/photo-1597724982185-3cbab60421c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxhdXRvbW90aXZlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHxibHVlfDE3NTM0NDk0MzJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Professioneller KFZ-Gutachter bei der Arbeit"
                className="w-full h-64 md:h-80 lg:h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-blue-900/20"></div>
            </div>
            
            {/* Floating Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 premium-glass p-3 md:p-4 rounded-2xl shadow-xl premium-border luxury-glow"
            >
              <div className="flex items-center space-x-2 md:space-x-3">
                <div className="premium-glass p-1.5 md:p-2 rounded-full">
                  <Users className="w-4 h-4 md:w-5 md:h-5 premium-icon" />
                </div>
                <div>
                  <p className="font-semibold gold-text text-xs md:text-sm">500+ Kunden</p>
                  <p className="text-xs text-gray-400">Vertrauen uns bereits</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const ProblemSolution = () => {
  return (
    <AnimatedSection className="py-8 md:py-12 bg-gradient-to-b from-slate-800 to-slate-900 premium-pattern">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Warum ein <span className="gold-text">professionelles Gutachten</span>?
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Ein unabhängiges Gutachten macht den <span className="gold-text-subtle">entscheidenden Unterschied</span> für Ihre finanzielle Sicherheit
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Without Expert */}
          <motion.div variants={fadeInUp} className="premium-glass rounded-3xl p-8 premium-border">
            <div className="flex items-center mb-6">
              <AlertTriangle className="w-8 h-8 text-red-500 mr-3" />
              <h3 className="text-xl font-bold text-white">Ohne Gutachten</h3>
            </div>
            <img
              src="https://images.unsplash.com/photo-1709836882177-1f2c7d901ab9"
              alt="Fahrzeugschaden ohne Gutachten"
              className="w-full h-32 object-cover rounded-2xl mb-6"
            />
            <ul className="space-y-4">
              <li className="flex items-start">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mt-1 mr-3">
                  <span className="text-white text-sm">✕</span>
                </div>
                <span className="text-gray-300">Risiko von Unterbewertung durch Versicherung</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mt-1 mr-3">
                  <span className="text-white text-sm">✕</span>
                </div>
                <span className="text-gray-300">Verzögerung der Schadensabwicklung</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mt-1 mr-3">
                  <span className="text-white text-sm">✕</span>
                </div>
                <span className="text-gray-300">Streit mit Versicherung oder Leasingfirma</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mt-1 mr-3">
                  <span className="text-white text-sm">✕</span>
                </div>
                <span className="text-gray-300">Finanzielle Verluste durch Unwissen</span>
              </li>
            </ul>
          </motion.div>

          {/* With Expert */}
          <motion.div variants={fadeInUp} className="premium-glass rounded-3xl p-8 luxury-glow premium-border">
            <div className="flex items-center mb-6">
              <CheckCircle className="w-8 h-8 text-green-500 mr-3" />
              <h3 className="text-xl font-bold gold-text">Mit Gutachten</h3>
            </div>
            <img
              src="https://images.unsplash.com/photo-1649768870222-17848797d6b4"
              alt="Professioneller KFZ-Gutachter"
              className="w-full h-32 object-cover rounded-2xl mb-6"
            />
            <ul className="space-y-4">
              <li className="flex items-start">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1 mr-3">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-gray-300">Transparenz und <span className="gold-text-subtle">Klarheit</span> über Schäden</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1 mr-3">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-gray-300">Finanzielle <span className="gold-text-subtle">Sicherheit</span> durch faire Bewertung</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1 mr-3">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-gray-300"><span className="gold-text-subtle">Gerichtsfeste</span> Dokumentation</span>
              </li>
              <li className="flex items-start">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mt-1 mr-3">
                  <span className="text-white text-sm">✓</span>
                </div>
                <span className="text-gray-300">Schnelle und <span className="gold-text-subtle">professionelle</span> Abwicklung</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

const Services = () => {
  const services = [
    {
      icon: <Car className="w-8 h-8" />,
      title: "Schadengutachten",
      description: "Nach Unfällen – gerichtsfest & für Versicherungen",
      image: "https://images.unsplash.com/photo-1597328290883-50c5787b7c7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxjYXIlMjBhY2NpZGVudHxlbnwwfHx8fDE3NTM5OTUwNjN8MA&ixlib=rb-4.1.0&q=85",
      features: ["Unfallschäden", "Hagelschäden", "Vandalismusschäden", "Gerichtsfeste Dokumentation"]
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Wertgutachten",
      description: "Für An- und Verkauf – objektiv & nachvollziehbar",
      image: "https://images.unsplash.com/photo-1527877218688-f3f0f876a186",
      features: ["Fahrzeugbewertung", "Marktwertermittlung", "Kaufberatung", "Objektive Einschätzung"]
    },
    {
      icon: <Clipboard className="w-8 h-8" />,
      title: "Leasingrücknahmen",
      description: "Vor Rückgabe – Schäden dokumentieren & Kosten vermeiden",
      image: "https://images.unsplash.com/photo-1521791055366-0d553872125f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxsZWFzZSUyMGFncmVlbWVudHxlbnwwfHx8fDE3NTM5OTUzMzZ8MA&ixlib=rb-4.1.0&q=85",
      features: ["Rückgabe-Inspektion", "Kostenvermeidung", "Schadendokumentation", "Leasingberatung"]
    }
  ];

  return (
    <AnimatedSection className="py-20 bg-gradient-to-b from-slate-900 to-slate-800 premium-pattern">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Unsere <span className="gold-text">Leistungen</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            <span className="gold-text-subtle">Professionelle KFZ-Gutachten</span> für jeden Bedarf – unabhängig, gerichtsfest und zuverlässig
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="group premium-glass rounded-3xl p-4 md:p-6 premium-border luxury-glow hover:-translate-y-2 transition-all duration-300"
            >
              <div className="mb-4">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-32 md:h-32 object-cover rounded-2xl"
                />
              </div>
              
              <div className="flex items-center mb-3 md:mb-4">
                <div className="gold-button w-10 h-10 md:w-12 md:h-12 rounded-full shadow-lg mr-3 flex items-center justify-center flex-shrink-0">
                  {React.cloneElement(service.icon, { className: "w-5 h-5 md:w-8 md:h-8" })}
                </div>
                <h3 className="text-lg md:text-xl font-bold gold-text">{service.title}</h3>
              </div>
              
              <p className="text-gray-300 mb-4 md:mb-6 text-sm md:text-base">{service.description}</p>
              
              <ul className="space-y-1 md:space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-xs md:text-sm text-gray-300">
                    <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500 mr-2 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      title: "Termin buchen",
      description: "Vereinbaren Sie einen Termin über unser Online-System oder telefonisch",
      icon: <Calendar className="w-6 h-6" />
    },
    {
      number: "02", 
      title: "Begutachtung vor Ort",
      description: "Unsere Experten kommen zu Ihnen oder per Foto-Dokumentation",
      icon: <Car className="w-6 h-6" />
    },
    {
      number: "03",
      title: "Erstellung & Versand",
      description: "Professionelle Gutachten-Erstellung und digitaler Versand",
      icon: <FileText className="w-6 h-6" />
    },
    {
      number: "04",
      title: "Weitergabe",
      description: "Übermittlung an Versicherung oder Leasingfirma auf Wunsch",
      icon: <Users className="w-6 h-6" />
    }
  ];

  return (
    <AnimatedSection className="py-20 bg-gradient-to-b from-slate-800 to-slate-900 premium-pattern">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            So <span className="gold-text">funktioniert's</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Vier einfache Schritte zu Ihrem <span className="gold-text-subtle">professionellen KFZ-Gutachten</span>
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="relative text-center"
            >
              <div className="premium-glass rounded-3xl p-6 premium-border luxury-glow hover:-translate-y-2 transition-all duration-300">
                <div className="relative mb-6">
                  <div className="gold-button w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    {step.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center premium-border">
                    <span className="gold-text text-sm font-bold">{step.number}</span>
                  </div>
                </div>
                
                <h3 className="text-lg font-bold gold-text mb-3">{step.title}</h3>
                <p className="text-gray-300 text-sm">{step.description}</p>
              </div>
              
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                  <ArrowRight className="w-6 h-6 premium-icon" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

const Testimonials = () => {
  const testimonials = [
    {
      name: "Michael Weber",
      role: "Privatperson",
      image: "https://images.unsplash.com/photo-1527877218688-f3f0f876a186",
      rating: 5,
      text: "Crashcontrol hat mir nach meinem Unfall sehr geholfen. Das Gutachten war schnell fertig und die Versicherung hat ohne Probleme bezahlt. Sehr professionell!"
    },
    {
      name: "Sarah Mueller",
      role: "Firmenkunde",
      image: "https://images.unsplash.com/photo-1649768870222-17848797d6b4",
      rating: 5,
      text: "Für unser Firmenfahrzeug brauchten wir ein Wertgutachten. Die Beratung war kompetent und der Service ausgezeichnet. Absolute Empfehlung!"
    },
    {
      name: "Thomas Klein",
      role: "Leasingkunde",
      image: "https://images.unsplash.com/photo-1595787142948-569014817b2f",
      rating: 5,
      text: "Dank Crashcontrol konnte ich hohe Kosten bei der Leasingrückgabe vermeiden. Das Gutachten war detailliert und hat mir viel Geld gespart."
    }
  ];

  return (
    <AnimatedSection className="py-20 bg-gradient-to-b from-slate-900 to-slate-800 premium-pattern">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Was unsere <span className="gold-text">Kunden</span> sagen
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Vertrauen Sie auf die Erfahrungen von über <span className="gold-text-subtle">500 zufriedenen Kunden</span>
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="premium-glass rounded-3xl p-6 premium-border luxury-glow hover:-translate-y-2 transition-all duration-300"
            >
              <div className="flex items-center mb-6">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover mr-4"
                />
                <div>
                  <h4 className="font-bold gold-text">{testimonial.name}</h4>
                  <p className="text-sm text-gray-400">{testimonial.role}</p>
                </div>
              </div>
              
              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 premium-icon fill-current" />
                ))}
              </div>
              
              <p className="text-gray-300 italic">"{testimonial.text}"</p>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

const Contact = () => {
  return (
    <AnimatedSection className="py-20 bg-gradient-to-b from-slate-800 to-slate-900 premium-pattern">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Kontakt & <span className="gold-text">Standort</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Besuchen Sie uns in <span className="gold-text-subtle">Fürth</span> oder kontaktieren Sie uns für einen Termin
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
          <motion.div variants={fadeInUp} className="premium-glass rounded-3xl p-6 md:p-8 luxury-glow premium-border">
            <h3 className="text-xl md:text-2xl font-bold gold-text mb-6 md:mb-8 text-center">Kontaktinformationen</h3>
            
            <div className="space-y-6 md:space-y-0 md:grid md:grid-cols-2 md:gap-8">
              <div className="space-y-4 md:space-y-6">
                <div className="flex items-center">
                  <div className="gold-button p-2 md:p-3 rounded-full mr-3 md:mr-4 flex-shrink-0">
                    <MapPin className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <p className="font-semibold gold-text-subtle text-sm md:text-base">Adresse</p>
                    <p className="text-gray-300 text-sm md:text-base">Leyher Str. 57, 90763 Fürth</p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="gold-button p-2 md:p-3 rounded-full mr-3 md:mr-4 flex-shrink-0">
                    <Phone className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <p className="font-semibold gold-text-subtle text-sm md:text-base">Telefon</p>
                    <a href="tel:+491623517659" className="text-gray-300 hover:text-white transition-colors text-sm md:text-base">
                      +49 162 3517659
                    </a>
                  </div>
                </div>
              </div>

              <div className="space-y-4 md:space-y-6">
                <div className="flex items-center">
                  <div className="gold-button p-2 md:p-3 rounded-full mr-3 md:mr-4 flex-shrink-0">
                    <Mail className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <p className="font-semibold gold-text-subtle text-sm md:text-base">E-Mail</p>
                    <p className="text-gray-300 text-sm md:text-base break-all">gutachten.crashcontrol@gmx.de</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="gold-button p-2 md:p-3 rounded-full mr-3 md:mr-4 flex-shrink-0">
                    <Clock className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div>
                    <p className="font-semibold gold-text-subtle text-sm md:text-base">Öffnungszeiten</p>
                    <p className="text-gray-300 text-sm md:text-base">24/7 erreichbar</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div variants={fadeInUp} className="premium-glass rounded-3xl p-6 md:p-8 luxury-glow premium-border">
            <h3 className="text-xl md:text-2xl font-bold gold-text mb-4 md:mb-6 text-center">Unser Standort</h3>
            <div className="relative h-64 md:h-96 rounded-2xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2598.8!2d10.9909!3d49.4747!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479f57c8c8c8c8c8%3A0x0!2sLeyher%20Str.%2057%2C%2090763%20F%C3%BCrth!5e0!3m2!1sde!2sde!4v1634567890123!5m2!1sde!2sde"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Crashcontrol Standort - Leyher Str. 57, 90763 Fürth"
              ></iframe>
            </div>
            <div className="mt-4 text-center">
              <p className="text-gray-300 text-sm md:text-base">
                <MapPin className="w-4 h-4 inline mr-1 premium-icon" />
                Leyher Str. 57, 90763 Fürth
              </p>
              <p className="text-xs md:text-sm text-gray-400 mt-2">
                Besuchen Sie uns vor Ort oder kontaktieren Sie uns für einen Termin bei Ihnen
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
};

const Footer = () => {
  return (
    <footer className="bg-gradient-to-b from-slate-900 to-black border-t border-gray-800 premium-pattern">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          <div className="col-span-2">
            <h3 className="text-2xl font-bold gold-text mb-4">Crashcontrol</h3>
            <p className="text-gray-400 mb-4">
              Ihr zuverlässiger Partner für <span className="gold-text-subtle">unabhängige KFZ-Gutachten</span> in Fürth und Umgebung. 
              Professionell, schnell und gerichtsfest.
            </p>
            <div className="flex space-x-1 md:space-x-4">
              <a 
                href="https://www.instagram.com/crashcontrol_gutachten?igsh=MWpyZDNoNHNvZTE0bw%3D%3D&utm_source=qr" 
                target="_blank"
                rel="noopener noreferrer"
                className="premium-glass p-1 md:p-2 rounded-full text-gray-400 hover:text-pink-500 transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-3 h-3 md:w-5 md:h-5" />
              </a>
              <a 
                href="https://tiktok.com/@crash_control" 
                target="_blank"
                rel="noopener noreferrer"
                className="premium-glass p-1 md:p-2 rounded-full text-gray-400 hover:text-white transition-colors duration-300"
                aria-label="TikTok"
              >
                <svg className="w-3 h-3 md:w-5 md:h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.10z"/>
                </svg>
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold gold-text-subtle mb-4">Leistungen</h4>
            <ul className="space-y-2 text-gray-400">
              <li>Schadengutachten</li>
              <li>Wertgutachten</li>
              <li>Leasingrücknahmen</li>
              <li>Beratung</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold gold-text-subtle mb-4">Rechtliches</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <a 
                  href="/impressum"
                  className="hover:text-white transition-colors"
                >
                  Impressum
                </a>
              </li>
              <li>
                <a 
                  href="/agb"
                  className="hover:text-white transition-colors"
                >
                  AGB
                </a>
              </li>
              <li>
                <a 
                  href="/datenschutz"
                  className="hover:text-white transition-colors"
                >
                  Datenschutz
                </a>
              </li>
              <li>
                <a 
                  href="/dsgvo"
                  className="hover:text-white transition-colors"
                >
                  DSGVO
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="corporate-divider my-8"></div>
        
        <div className="text-center text-gray-500">
          <p>&copy; 2025 <span className="gold-text-subtle">Crashcontrol</span> - Uasar Maulud. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </footer>
  );
};

// DSGVO Component
const DSGVO = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen premium-pattern pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center premium-glass px-4 py-2 rounded-xl text-white mb-6 hover:luxury-glow transition-all duration-300"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Zurück zur Startseite
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            <span className="gold-text">📄 Informationspflichten nach Art. 13 DSGVO</span>
          </h1>
        </div>

        {/* DSGVO Content */}
        <div className="premium-glass rounded-3xl p-8 md:p-12 premium-border luxury-glow">
          <div className="text-white space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-white mb-4">Crash Control – Uasar Maulud</h2>
              <p className="text-gray-300 leading-relaxed">
                Diese Information dient der Erfüllung der Transparenzpflichten nach Art. 13 DSGVO, wenn du personenbezogene Daten z. B. im Rahmen der Gutachtenerstellung verarbeitest:
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">1. Name und Kontaktdaten des Verantwortlichen</h3>
              <div className="text-gray-300 space-y-1">
                <p className="font-semibold text-white">Crash Control</p>
                <p>Inhaber: Uasar Maulud</p>
                <p>Leyherstraße 57</p>
                <p>90763 Fürth</p>
                <p>E-Mail: <a href="mailto:gutachten.crashcontrol@gmx.de" className="gold-text-subtle hover:text-white transition-colors">gutachten.crashcontrol@gmx.de</a></p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">2. Zwecke und Rechtsgrundlagen der Verarbeitung</h3>
              <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4 mb-4">
                <li>Erstellung von Kfz-Gutachten</li>
                <li>Kommunikation mit Kunden, Versicherungen, Rechtsanwälten</li>
                <li>Abrechnung und steuerliche Pflichten</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mb-2 font-semibold">Rechtsgrundlagen:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                <li>Art. 6 Abs. 1 lit. b DSGVO (Vertrag)</li>
                <li>Art. 6 Abs. 1 lit. c DSGVO (gesetzliche Verpflichtung)</li>
                <li>Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">3. Empfänger der Daten</h3>
              <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                <li>ggf. Versicherungen</li>
                <li>ggf. beauftragte Rechtsanwälte</li>
                <li>Steuerberater / Finanzbehörden</li>
                <li>Hosting-Anbieter (bei Websitekontakt)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">4. Dauer der Speicherung</h3>
              <p className="text-gray-300 leading-relaxed">
                Die Daten werden so lange gespeichert, wie es zur Vertragsabwicklung, zur Erfüllung gesetzlicher Aufbewahrungsfristen oder zur Geltendmachung von Ansprüchen erforderlich ist.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">5. Ihre Rechte</h3>
              <p className="text-gray-300 leading-relaxed mb-2">Sie haben folgende Rechte gemäß DSGVO:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                <li>Auskunft (Art. 15 DSGVO)</li>
                <li>Berichtigung (Art. 16 DSGVO)</li>
                <li>Löschung (Art. 17 DSGVO)</li>
                <li>Einschränkung (Art. 18 DSGVO)</li>
                <li>Widerspruch (Art. 21 DSGVO)</li>
                <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
                <li>Beschwerde bei der Aufsichtsbehörde (z. B. Bayerisches Landesamt für Datenschutzaufsicht)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">6. Pflicht zur Bereitstellung</h3>
              <p className="text-gray-300 leading-relaxed">
                Die Bereitstellung personenbezogener Daten ist zur Durchführung des Auftrags erforderlich. Ohne diese Daten kann kein Gutachten erstellt werden.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">7. Automatisierte Entscheidungsfindung / Profiling</h3>
              <p className="text-gray-300 leading-relaxed">
                Es findet keine automatisierte Entscheidungsfindung oder Profiling statt.
              </p>
            </div>

            <div className="border-t border-gray-700 pt-6 mt-8">
              <div className="text-center">
                <p className="text-gray-300">
                  Weitere Fragen? Kontaktieren Sie uns: <a href="mailto:gutachten.crashcontrol@gmx.de" className="gold-text-subtle hover:text-white transition-colors font-semibold">gutachten.crashcontrol@gmx.de</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Datenschutz Component
const Datenschutz = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen premium-pattern pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center premium-glass px-4 py-2 rounded-xl text-white mb-6 hover:luxury-glow transition-all duration-300"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Zurück zur Startseite
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            <span className="gold-text">Datenschutzerklärung</span>
          </h1>
        </div>

        {/* Datenschutz Content */}
        <div className="premium-glass rounded-3xl p-8 md:p-12 premium-border luxury-glow">
          <div className="text-white space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-white mb-2">Crash Control – Uasar Maulud</h2>
              <p className="text-gray-300">Stand: August 2025</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">1. Verantwortlicher im Sinne der DSGVO</h3>
              <div className="text-gray-300 space-y-1">
                <p className="font-semibold text-white">Crash Control</p>
                <p>Inhaber: Uasar Maulud</p>
                <p>Leyherstraße 57</p>
                <p>90763 Fürth</p>
                <p>E-Mail: <a href="mailto:gutachten.crashcontrol@gmx.de" className="gold-text-subtle hover:text-white transition-colors">gutachten.crashcontrol@gmx.de</a></p>
                <p>Website: <a href="http://crash-control.com" className="gold-text-subtle hover:text-white transition-colors">www.crash-control.com</a></p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">2. Erhebung und Speicherung personenbezogener Daten</h3>
              <p className="text-gray-300 leading-relaxed mb-3">
                Beim Besuch unserer Website werden automatisch folgende Daten durch den Webserver erfasst (Server-Logfiles):
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4 mb-3">
                <li>IP-Adresse des Endgeräts</li>
                <li>Datum und Uhrzeit der Anfrage</li>
                <li>Zugriffsstatus/HTTP-Statuscode</li>
                <li>übertragene Datenmenge</li>
                <li>Website, von der die Anfrage kommt</li>
                <li>Browsertyp, Sprache und Version</li>
                <li>Betriebssystem</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Diese Daten sind technisch notwendig, um die Website korrekt anzuzeigen, und dienen der Systemsicherheit.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">3. Kontaktaufnahme / Auftragsverarbeitung</h3>
              <p className="text-gray-300 leading-relaxed mb-3">
                Wenn Sie mit uns per E-Mail, Telefon oder Formular Kontakt aufnehmen, werden die von Ihnen übermittelten Daten (z. B. Name, Telefonnummer, E-Mail-Adresse, Fahrzeuginformationen, Versicherungsdaten) gespeichert, um Ihr Anliegen zu bearbeiten und ggf. ein Gutachten zu erstellen.
              </p>
              <p className="text-gray-300 leading-relaxed">
                <span className="font-semibold">Rechtsgrundlage:</span> Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung und -durchführung)
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">4. Weitergabe von Daten an Dritte</h3>
              <p className="text-gray-300 leading-relaxed mb-3">
                Ihre Daten werden grundsätzlich nicht an Dritte weitergegeben, außer:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4 mb-3">
                <li>Sie haben ausdrücklich eingewilligt (Art. 6 Abs. 1 lit. a DSGVO)</li>
                <li>die Weitergabe ist zur Vertragserfüllung notwendig (z. B. an Versicherungen, Rechtsanwälte)</li>
                <li>es besteht eine gesetzliche Verpflichtung (Art. 6 Abs. 1 lit. c DSGVO)</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Eine Übermittlung in Drittstaaten außerhalb der EU erfolgt nicht.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">5. Speicherdauer</h3>
              <p className="text-gray-300 leading-relaxed">
                Wir speichern Ihre personenbezogenen Daten nur so lange, wie es zur Erfüllung des jeweiligen Zwecks notwendig ist oder gesetzliche Aufbewahrungsfristen (z. B. steuerrechtlich) es erfordern.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">6. Ihre Rechte als betroffene Person</h3>
              <p className="text-gray-300 leading-relaxed mb-2">Sie haben das Recht:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                <li>auf Auskunft über Ihre gespeicherten Daten (Art. 15 DSGVO)</li>
                <li>auf Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
                <li>auf Löschung („Recht auf Vergessenwerden") Ihrer Daten (Art. 17 DSGVO)</li>
                <li>auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
                <li>auf Datenübertragbarkeit (Art. 20 DSGVO)</li>
                <li>auf Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
                <li>auf Beschwerde bei einer Aufsichtsbehörde (z. B. BayLDA)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">7. Sicherheit</h3>
              <p className="text-gray-300 leading-relaxed">
                Wir treffen angemessene technische und organisatorische Maßnahmen zum Schutz Ihrer Daten, insbesondere vor unbefugtem Zugriff, Verlust oder Manipulation. Die Datenübertragung über unsere Website erfolgt TLS-verschlüsselt (https).
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">8. Cookies & Tracking</h3>
              <p className="text-gray-300 leading-relaxed">
                Diese Website verwendet keine Cookies oder Tracking-Dienste wie Google Analytics. Sollte sich dies in Zukunft ändern, wird diese Erklärung entsprechend angepasst.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">9. Änderung dieser Datenschutzerklärung</h3>
              <p className="text-gray-300 leading-relaxed">
                Wir behalten uns vor, diese Datenschutzerklärung bei Bedarf anzupassen, um sie an rechtliche Anforderungen oder Änderungen unserer Dienstleistungen anzupassen. Die jeweils aktuelle Fassung finden Sie stets auf unserer Website.
              </p>
            </div>

            <div className="border-t border-gray-700 pt-6 mt-8">
              <div className="text-center">
                <h4 className="text-lg font-semibold gold-text-subtle mb-3">Fragen zum Datenschutz?</h4>
                <p className="text-gray-300">
                  Schreiben Sie uns an: <a href="mailto:gutachten.crashcontrol@gmx.de" className="gold-text-subtle hover:text-white transition-colors font-semibold">gutachten.crashcontrol@gmx.de</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// AGB Component
const AGB = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen premium-pattern pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center premium-glass px-4 py-2 rounded-xl text-white mb-6 hover:luxury-glow transition-all duration-300"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Zurück zur Startseite
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            <span className="gold-text">Allgemeine Geschäftsbedingungen</span>
          </h1>
        </div>

        {/* AGB Content */}
        <div className="premium-glass rounded-3xl p-8 md:p-12 premium-border luxury-glow">
          <div className="text-white space-y-8">
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-white mb-2">Crash Control – Uasar Maulud</h2>
              <p className="text-gray-300">Leyherstraße 57, 90763 Fürth</p>
              <p className="text-gray-300">Stand: August 2025</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">1. Geltungsbereich</h3>
              <p className="text-gray-300 leading-relaxed">
                Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Verträge zwischen dem Einzelunternehmen Crash Control, Inhaber Uasar Maulud, Leyherstraße 57, 90763 Fürth (nachfolgend "Anbieter"), und seinen Auftraggebern (Verbrauchern und Unternehmern) über die Erbringung von Kfz-Gutachterdienstleistungen, insbesondere Unfall- und Schadensgutachten sowie Leasingrücknahmebewertungen.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">2. Leistungen des Anbieters</h3>
              <p className="text-gray-300 leading-relaxed mb-3">Crash Control bietet folgende Dienstleistungen an:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                <li>Erstellung von Kfz-Schadens- und Unfallgutachten</li>
                <li>Erstellung von Leasingrücknahme-Gutachten</li>
                <li>Fahrzeugbewertung im Auftrag Dritter</li>
                <li>Beratung im Rahmen der Begutachtung</li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-3">
                Die Gutachten werden auf Basis objektiver Fahrzeugbewertungen durch den Anbieter oder beauftragte Sachverständige erstellt. Ein konkreter Erfolg (z. B. Anerkennung durch eine Versicherung) ist nicht geschuldet.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">3. Vertragsschluss</h3>
              <p className="text-gray-300 leading-relaxed">
                Der Vertrag kommt durch individuelle Absprache – schriftlich, mündlich oder telefonisch – zwischen dem Auftraggeber und dem Anbieter zustande. Eine verbindliche Auftragserteilung gilt auch bei mündlicher Beauftragung vor Ort oder telefonisch.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">4. Vergütung und Abrechnung</h3>
              <p className="text-gray-300 leading-relaxed mb-3">
                Die Vergütung der Dienstleistungen richtet sich nach dem Umfang des erstellten Gutachtens und orientiert sich an den allgemein anerkannten Honorartabellen der Kfz-Gutachterbranche.
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4 mb-3">
                <li>Bei Haftpflichtschäden erfolgt die Abrechnung in der Regel direkt mit der Versicherung des Unfallverursachers.</li>
                <li>Bei Leasingrückgaben, Bagatellschäden oder Privatgutachten kann eine direkte Rechnungsstellung an den Auftraggeber erfolgen.</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                Individuelle Vereinbarungen zur Zahlung, Höhe der Vergütung und Selbstbeteiligung sind möglich und bedürfen der schriftlichen Bestätigung.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">5. Mitwirkungspflichten des Auftraggebers</h3>
              <p className="text-gray-300 leading-relaxed">
                Der Auftraggeber ist verpflichtet, dem Anbieter alle zur Begutachtung erforderlichen Informationen, Unterlagen und Zugang zum Fahrzeug vollständig und rechtzeitig bereitzustellen.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">6. Haftung</h3>
              <p className="text-gray-300 leading-relaxed mb-3">
                Der Anbieter haftet uneingeschränkt für Schäden aus der Verletzung des Lebens, des Körpers oder der Gesundheit, die auf einer fahrlässigen oder vorsätzlichen Pflichtverletzung beruhen.
              </p>
              <p className="text-gray-300 leading-relaxed mb-2">Im Übrigen ist die Haftung ausgeschlossen, insbesondere für:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1 ml-4">
                <li>Verzögerungen durch Krankheit oder Unfall des Gutachters</li>
                <li>Ausfall aufgrund technischer Störungen (z. B. Softwarefehler)</li>
                <li>Datenverlust, sofern dieser nicht grob fahrlässig verursacht wurde</li>
                <li>mittelbare Schäden, z. B. aufgrund fehlerhafter Interpretation des Gutachtens durch Dritte</li>
                <li>Nutzung des Gutachtens zur Einschätzung der Verkehrssicherheit</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">7. Widerrufsrecht</h3>
              <p className="text-gray-300 leading-relaxed">
                Für individuell erstellte Gutachten besteht gemäß § 312g Abs. 2 Nr. 1 BGB kein Widerrufsrecht, da es sich um eine Dienstleistung handelt, die auf die persönlichen Anforderungen des Auftraggebers zugeschnitten ist.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">8. Datenschutz</h3>
              <p className="text-gray-300 leading-relaxed">
                Personenbezogene Daten werden ausschließlich zur Abwicklung des Auftrags, zur Gutachtenerstellung, Kommunikation und ggf. zur Abrechnung mit Dritten (z. B. Versicherungen) verwendet. Weitere Informationen entnehmen Sie bitte der Datenschutzerklärung auf dieser Website.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-3">9. Schlussbestimmungen</h3>
              <p className="text-gray-300 leading-relaxed mb-3">
                Sollte eine Bestimmung dieser AGB unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Es gilt das Recht der Bundesrepublik Deutschland. Gerichtsstand ist Fürth, soweit gesetzlich zulässig.
              </p>
            </div>

            <div className="border-t border-gray-700 pt-6 mt-8">
              <div className="text-center text-gray-300">
                <p className="font-semibold text-white mb-1">Crash Control – Uasar Maulud</p>
                <p>Leyherstraße 57</p>
                <p>90763 Fürth</p>
                <p className="mt-2">
                  <a href="mailto:gutachten.crashcontrol@gmx.de" className="gold-text-subtle hover:text-white transition-colors">
                    gutachten.crashcontrol@gmx.de
                  </a>
                </p>
                <p>
                  <a href="http://crash-control.com" className="gold-text-subtle hover:text-white transition-colors">
                    www.crash-control.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Impressum Component
const Impressum = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen premium-pattern pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center premium-glass px-4 py-2 rounded-xl text-white mb-6 hover:luxury-glow transition-all duration-300"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Zurück zur Startseite
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            <span className="gold-text">Impressum</span>
          </h1>
        </div>

        {/* Impressum Content */}
        <div className="premium-glass rounded-3xl p-8 md:p-12 premium-border luxury-glow">
          <div className="text-white space-y-6">
            <div>
              <h2 className="text-xl font-bold gold-text mb-4">Angaben gemäß § 5 TMG</h2>
              
              <div className="space-y-2 text-gray-300">
                <p className="font-semibold text-white">Crash Control</p>
                <p>Inhaber: Uasar Maulud</p>
                <p>Leyher Straße 57</p>
                <p>90763 Fürth</p>
                <p>Deutschland</p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-2">Tätigkeit:</h3>
              <p className="text-gray-300">Kfz-Sachverständiger (Gutachter für Kfz-Schäden und -Bewertungen)</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-2">Kontakt:</h3>
              <div className="space-y-1 text-gray-300">
                <p>Telefon: <a href="tel:+491623517659" className="gold-text-subtle hover:text-white transition-colors">+49 162 3517659</a></p>
                <p>E-Mail: <a href="mailto:gutachten.crashcontrol@gmx.de" className="gold-text-subtle hover:text-white transition-colors">gutachten.crashcontrol@gmx.de</a></p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold gold-text-subtle mb-2">Online-Streitbeilegung gemäß Art. 14 Abs. 1 ODR-VO</h3>
              <div className="space-y-2 text-gray-300">
                <p>
                  <a 
                    href="https://ec.europa.eu/consumers/odr" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="gold-text-subtle hover:text-white transition-colors underline"
                  >
                    https://ec.europa.eu/consumers/odr
                  </a>
                </p>
                <p>Unsere E-Mail-Adresse finden Sie oben im Impressum.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TerminBuchen = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Verfügbare Zeiten (24/7)
  const availableTimes = [
    '00:00', '00:30', '01:00', '01:30', '02:00', '02:30', '03:00', '03:30',
    '04:00', '04:30', '05:00', '05:30', '06:00', '06:30', '07:00', '07:30',
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
    '16:00', '16:30', '17:00', '17:30', '18:00', '18:30', '19:00', '19:30',
    '20:00', '20:30', '21:00', '21:30', '22:00', '22:30', '23:00', '23:30'
  ];

  // Bereits gebuchte Termine (24/7)
  const bookedTimes = {
    '2024-12-20': ['02:00', '14:30', '22:00'],
    '2024-12-21': ['05:00', '11:30', '18:00'],
    '2024-12-23': ['00:30', '09:00', '20:30']
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    while (current <= lastDay || current.getDay() !== 0) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const isWeekday = (date) => {
    return true; // 24/7 verfügbar
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getAvailableTimesForDate = (date) => {
    const dateString = formatDate(date);
    const booked = bookedTimes[dateString] || [];
    return availableTimes.filter(time => !booked.includes(time));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleDateSelect = (date) => {
    if (isWeekday(date) && !isPastDate(date)) {
      setSelectedDate(date);
      setSelectedTime(null);
    }
  };

  const handleBooking = async () => {
    if (selectedDate && selectedTime) {
      try {
        const formattedDate = selectedDate.toLocaleDateString('de-DE');
        
        const appointmentData = {
          date: formattedDate,
          time: selectedTime,
          name: "Kunde", // Kann später durch Formular erweitert werden
          email: null,
          phone: null,
          message: "Terminbuchung über Website"
        };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/book-appointment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(appointmentData),
        });

        const result = await response.json();

        if (result.success) {
          alert(`✅ Termin erfolgreich gebucht!\n\n📅 Datum: ${formattedDate}\n🕐 Uhrzeit: ${selectedTime} Uhr\n\n📧 Sie erhalten eine E-Mail-Bestätigung mit Kalender-Datei für Outlook.`);
          // Reset selection
          setSelectedDate(null);
          setSelectedTime(null);
        } else {
          alert(`❌ Terminbuchung fehlgeschlagen:\n${result.message}`);
        }
      } catch (error) {
        alert(`❌ Fehler bei der Terminbuchung:\n${error.message}`);
      }
    }
  };

  const monthNames = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  const dayNames = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

  return (
    <div className="min-h-screen premium-pattern pt-16 md:pt-20">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center premium-glass px-3 md:px-4 py-2 rounded-xl text-white mb-4 md:mb-6 hover:luxury-glow transition-all duration-300 text-sm md:text-base"
          >
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 mr-1" />
            Zurück zur Startseite
          </button>
          
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 md:mb-4">
            <span className="gold-text">Termin</span> <span className="text-white">buchen</span>
          </h1>
          <p className="text-sm md:text-lg text-gray-300 max-w-2xl mx-auto px-4">
            Wählen Sie Ihren Wunschtermin für ein kostenloses Erstgespräch oder eine Begutachtung - <span className="gold-text-subtle">24/7 verfügbar</span>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12">
          {/* Kalender */}
          <div className="premium-glass rounded-3xl p-4 md:p-8 premium-border luxury-glow">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-lg md:text-xl font-bold gold-text">Verfügbare Termine</h2>
              <div className="flex items-center space-x-1 md:space-x-2">
                <button
                  onClick={prevMonth}
                  className="p-1.5 md:p-2 hover:bg-gray-800 rounded-full transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 premium-icon" />
                </button>
                <span className="text-sm md:text-lg font-semibold gold-text-subtle min-w-[120px] text-center">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </span>
                <button
                  onClick={nextMonth}
                  className="p-1.5 md:p-2 hover:bg-gray-800 rounded-full transition-colors"
                >
                  <ChevronRight className="w-4 h-4 md:w-5 md:h-5 premium-icon" />
                </button>
              </div>
            </div>

            {/* Kalender Grid */}
            <div className="grid grid-cols-7 gap-1 mb-3 md:mb-4">
              {dayNames.map(day => (
                <div key={day} className="text-center text-xs md:text-sm font-medium text-gray-400 p-1 md:p-2">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth(currentMonth).map((date, index) => {
                const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                const isSelectable = isWeekday(date) && !isPastDate(date) && isCurrentMonth;
                const isSelected = selectedDate && formatDate(date) === formatDate(selectedDate);
                const availableCount = isSelectable ? getAvailableTimesForDate(date).length : 0;

                return (
                  <button
                    key={index}
                    onClick={() => handleDateSelect(date)}
                    disabled={!isSelectable}
                    className={`
                      p-1.5 md:p-2 text-xs md:text-sm rounded-lg transition-all duration-200 relative
                      ${isSelected 
                        ? 'gold-button shadow-lg' 
                        : isSelectable 
                          ? 'hover:bg-gray-800 text-white premium-border' 
                          : 'text-gray-600 cursor-not-allowed'
                      }
                      ${isToday(date) && !isSelected ? 'ring-1 md:ring-2 ring-yellow-500' : ''}
                      ${!isCurrentMonth ? 'text-gray-700' : ''}
                    `}
                  >
                    {date.getDate()}
                    {isSelectable && availableCount > 0 && (
                      <div className="absolute -top-0.5 -right-0.5 md:-top-1 md:-right-1 w-3 h-3 md:w-4 md:h-4 bg-green-500 rounded-full flex items-center justify-center">
                        <span className="text-xs text-white">{availableCount}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 md:mt-6 flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-400">
              <div className="flex items-center">
                <div className="w-3 h-3 md:w-4 md:h-4 gold-button rounded mr-2"></div>
                <span>Ausgewählt</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 md:w-4 md:h-4 bg-green-500 rounded mr-2"></div>
                <span>Verfügbare Termine</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 md:w-4 md:h-4 bg-gray-600 rounded mr-2"></div>
                <span>Nicht verfügbar</span>
              </div>
            </div>
          </div>

          {/* Uhrzeiten & Buchung */}
          <div className="space-y-4 md:space-y-6">
            {/* Uhrzeiten */}
            <div className="premium-glass rounded-3xl p-4 md:p-8 premium-border luxury-glow">
              <h3 className="text-lg md:text-xl font-bold gold-text mb-4 md:mb-6">
                {selectedDate ? `Verfügbare Zeiten am ${selectedDate.toLocaleDateString('de-DE')}` : 'Wählen Sie zuerst ein Datum'}
              </h3>
              
              {selectedDate ? (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2 md:gap-3 max-h-64 md:max-h-96 overflow-y-auto">
                  {getAvailableTimesForDate(selectedDate).map(time => (
                    <button
                      key={time}
                      onClick={() => setSelectedTime(time)}
                      className={`
                        p-2 md:p-3 rounded-xl text-xs md:text-sm font-medium transition-all duration-200
                        ${selectedTime === time 
                          ? 'gold-button shadow-lg transform scale-105' 
                          : 'premium-glass text-gray-300 hover:text-white premium-border'
                        }
                      `}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-400 py-6 md:py-8">
                  <Clock className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-3 md:mb-4 text-gray-600" />
                  <p className="text-sm md:text-base">Bitte wählen Sie zuerst ein Datum aus dem Kalender</p>
                </div>
              )}
            </div>

            {/* Buchung */}
            <div className="premium-glass rounded-3xl p-4 md:p-8 premium-border luxury-glow">
              <h3 className="text-lg md:text-xl font-bold gold-text mb-4 md:mb-6">Termin bestätigen</h3>
              
              {selectedDate && selectedTime ? (
                <div className="space-y-3 md:space-y-4">
                  <div className="premium-glass rounded-2xl p-4 md:p-6 premium-border">
                    <h4 className="font-semibold gold-text-subtle mb-2 text-sm md:text-base">Ihr gewählter Termin:</h4>
                    <p className="text-sm md:text-lg text-gray-300">
                      <Calendar className="w-4 h-4 md:w-5 md:h-5 inline mr-2 premium-icon" />
                      {selectedDate.toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                    <p className="text-sm md:text-lg text-gray-300">
                      <Clock className="w-4 h-4 md:w-5 md:h-5 inline mr-2 premium-icon" />
                      {selectedTime} Uhr
                    </p>
                  </div>
                  
                  <button
                    onClick={handleBooking}
                    className="w-full gold-button py-3 md:py-4 rounded-xl font-semibold text-sm md:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    Termin verbindlich buchen
                  </button>
                </div>
              ) : (
                <div className="text-center text-gray-400 py-6 md:py-8">
                  <FileText className="w-8 h-8 md:w-12 md:h-12 mx-auto mb-3 md:mb-4 text-gray-600" />
                  <p className="text-sm md:text-base">Wählen Sie Datum und Uhrzeit für die Buchung</p>
                </div>
              )}
            </div>

            {/* Kontakt Info */}
            <div className="premium-glass rounded-3xl p-4 md:p-8 text-white premium-border luxury-glow">
              <h3 className="text-lg md:text-xl font-bold gold-text mb-3 md:mb-4">Haben Sie Fragen?</h3>
              <p className="mb-4 md:mb-6 text-gray-300 text-sm md:text-base">Rufen Sie uns direkt an für eine <span className="gold-text-subtle">sofortige Beratung</span></p>
              <a 
                href="tel:01623517959"
                className="inline-flex items-center gold-button px-4 md:px-6 py-2.5 md:py-3 rounded-xl font-semibold hover:shadow-lg transition-all text-sm md:text-base"
              >
                <PhoneCall className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                0162 3517959
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  return (
    <>
      <Hero />
      <ProblemSolution />
      <Services />
      <HowItWorks />
      <Testimonials />
      <Contact />
      <Footer />
    </>
  );
};

function App() {
  // AGGRESSIVE Badge removal on component mount
  useEffect(() => {
    const removeEmergentBadge = () => {
      const selectors = [
        '#emergent-badge',
        '[id*="emergent"]',
        '[class*="emergent"]',
        'a[href*="emergent"]',
        'a[href*="app.emergent.sh"]',
        '[data-emergent]',
        'div[style*="emergent"]'
      ];
      
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          if (el) {
            el.remove();
          }
        });
      });
      
      // Also remove any fixed positioned elements at bottom right
      const allElements = document.querySelectorAll('*');
      allElements.forEach(el => {
        const computed = window.getComputedStyle(el);
        if (computed.position === 'fixed' && 
            (computed.bottom === '20px' || computed.right === '20px') &&
            (el.textContent.includes('Made with') || el.textContent.includes('Emergent'))) {
          el.remove();
        }
      });
    };
    
    // Remove immediately
    removeEmergentBadge();
    
    // Keep checking every 500ms
    const interval = setInterval(removeEmergentBadge, 500);
    
    // Also remove on any DOM mutations
    const observer = new MutationObserver(removeEmergentBadge);
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
    
    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  return (
    <BrowserRouter>
      <div className="App">
        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/termin-buchen" element={<TerminBuchen />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/agb" element={<AGB />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/dsgvo" element={<DSGVO />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;